import React, { useState } from "react";
import {
  Settings as SettingsIcon,
  Globe,
  Users,
  Bell,
  ShieldCheck,
  Save,
  RotateCcw,
  LockKeyhole,
  CheckCircle2,
  Eye,
} from "lucide-react";
import "./Settings.css";

const initialSettings = {
  siteName: "Salem Kongu Matrimony",
  contactEmail: "info@salemkongumatrimony.com",
  phone: "",
  address: "Salem, Tamil Nadu, India",
  website: "",
  profileApproval: true,
  photoApproval: true,
  showContactDetails: false,
  allowProfileEdit: true,
  membershipDuration: "365",
  emailNotifications: true,
  smsNotifications: true,
  pushNotifications: true,
  newRegistrationAlert: true,
  paymentAlert: true,
  loginAlerts: true,
  twoFactorAuth: false,
  sessionTimeout: "30",
};

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    className={`settings-toggle ${checked ? "is-active" : ""}`}
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
  >
    <span />
  </button>
);

const SettingRow = ({
  title,
  description,
  checked,
  onChange,
}) => (
  <div className="setting-row">
    <div className="setting-row-content">
      <h4>{title}</h4>
      <p>{description}</p>
    </div>
    <Toggle checked={checked} onChange={onChange} />
  </div>
);

const Settings = () => {
  const [settings, setSettings] = useState(initialSettings);
  const [activeTab, setActiveTab] = useState("general");
  const [notice, setNotice] = useState("");

  const updateSetting = (key, value) => {
    setSettings((previous) => ({
      ...previous,
      [key]: value,
    }));
    setNotice("");
  };

  const saveSettings = (event) => {
    event.preventDefault();

    // Connect this handler to your settings API later.
    console.log("Settings to save:", settings);
    setNotice("Settings saved successfully in the current session.");
  };

  const resetSettings = () => {
    setSettings(initialSettings);
    setNotice("Settings have been reset to their default values.");
  };

  const tabs = [
    { id: "general", label: "General", icon: Globe },
    { id: "matrimony", label: "Matrimony", icon: Users },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "security", label: "Security", icon: ShieldCheck },
  ];

  const renderInput = (
    label,
    key,
    placeholder = "",
    type = "text"
  ) => (
    <div className="settings-field" key={key}>
      <label htmlFor={key}>{label}</label>
      <input
        id={key}
        type={type}
        value={settings[key]}
        placeholder={placeholder}
        onChange={(event) =>
          updateSetting(key, event.target.value)
        }
      />
    </div>
  );

  const renderSelect = (label, key, options) => (
    <div className="settings-field" key={key}>
      <label htmlFor={key}>{label}</label>
      <select
        id={key}
        value={settings[key]}
        onChange={(event) =>
          updateSetting(key, event.target.value)
        }
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="settings-page">
      <div className="settings-page-header">
        <div>
          <div className="settings-breadcrumb">
            Dashboard <span>/</span> Settings
          </div>
          <h1>Settings</h1>
          <p>Manage your matrimony platform preferences.</p>
        </div>

        <div className="settings-header-icon">
          <SettingsIcon size={24} />
        </div>
      </div>

      {notice && (
        <div className="settings-notice">
          <CheckCircle2 size={18} />
          <span>{notice}</span>
          <button
            type="button"
            onClick={() => setNotice("")}
            aria-label="Dismiss notification"
          >
            ×
          </button>
        </div>
      )}

      <div className="settings-layout">
        <aside className="settings-sidebar">
          <div className="settings-sidebar-title">
            <span className="settings-sidebar-icon">
              <SettingsIcon size={18} />
            </span>
            <div>
              <h3>Preferences</h3>
              <p>Configure your platform</p>
            </div>
          </div>

          <div className="settings-tab-list">
            {tabs.map((tab) => {
              const Icon = tab.icon;

              return (
                <button
                  type="button"
                  key={tab.id}
                  className={`settings-tab ${activeTab === tab.id ? "active" : ""
                    }`}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setNotice("");
                  }}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                  {activeTab === tab.id && (
                    <span className="settings-tab-indicator" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="settings-help-card">
            <ShieldCheck size={22} />
            <h4>Keep your platform secure</h4>
            <p>
              Review your security and profile approval preferences
              regularly.
            </p>
            <button
              type="button"
              onClick={() => setActiveTab("security")}
            >
              Review Security
            </button>
          </div>
        </aside>

        <form className="settings-content" onSubmit={saveSettings}>
          {activeTab === "general" && (
            <>
              <div className="settings-section-heading">
                <div className="settings-section-icon blue">
                  <Globe size={21} />
                </div>
                <div>
                  <h2>General Settings</h2>
                  <p>Manage your website information and contact details.</p>
                </div>
              </div>

              <div className="settings-card">
                <div className="settings-card-heading">
                  <h3>Platform Information</h3>
                  <p>Basic information displayed for your platform.</p>
                </div>

                <div className="settings-form-grid">
                  {renderInput(
                    "Platform Name",
                    "siteName",
                    "Enter platform name"
                  )}
                  {renderInput(
                    "Contact Email",
                    "contactEmail",
                    "Enter contact email",
                    "email"
                  )}
                  {renderInput(
                    "Contact Phone",
                    "phone",
                    "Enter phone number",
                    "tel"
                  )}
                  {renderInput(
                    "Website URL",
                    "website",
                    "https://example.com",
                    "url"
                  )}
                  <div className="settings-field full-width">
                    <label htmlFor="address">Office Address</label>
                    <textarea
                      id="address"
                      rows="3"
                      value={settings.address}
                      placeholder="Enter office address"
                      onChange={(event) =>
                        updateSetting("address", event.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === "matrimony" && (
            <>
              <div className="settings-section-heading">
                <div className="settings-section-icon purple">
                  <Users size={21} />
                </div>
                <div>
                  <h2>Matrimony Settings</h2>
                  <p>Control profile management and membership preferences.</p>
                </div>
              </div>

              <div className="settings-card">
                <div className="settings-card-heading">
                  <h3>Profile Management</h3>
                  <p>Configure how member profiles are handled.</p>
                </div>

                <SettingRow
                  title="Profile Approval"
                  description="Require admin approval before a new profile becomes visible."
                  checked={settings.profileApproval}
                  onChange={(value) =>
                    updateSetting("profileApproval", value)
                  }
                />

                <SettingRow
                  title="Photo Approval"
                  description="Require approval before member photos are displayed."
                  checked={settings.photoApproval}
                  onChange={(value) =>
                    updateSetting("photoApproval", value)
                  }
                />

                <SettingRow
                  title="Show Contact Details"
                  description="Allow contact details to be shown according to platform rules."
                  checked={settings.showContactDetails}
                  onChange={(value) =>
                    updateSetting("showContactDetails", value)
                  }
                />

                <SettingRow
                  title="Allow Profile Editing"
                  description="Allow members to update their profile information."
                  checked={settings.allowProfileEdit}
                  onChange={(value) =>
                    updateSetting("allowProfileEdit", value)
                  }
                />
              </div>

              <div className="settings-card">
                <div className="settings-card-heading">
                  <h3>Membership Preferences</h3>
                  <p>Set the default membership duration.</p>
                </div>

                {renderSelect("Default Membership Duration", "membershipDuration", [
                  { value: "30", label: "30 Days" },
                  { value: "90", label: "90 Days" },
                  { value: "180", label: "180 Days" },
                  { value: "365", label: "365 Days" },
                ])}
              </div>
            </>
          )}

          {activeTab === "notifications" && (
            <>
              <div className="settings-section-heading">
                <div className="settings-section-icon orange">
                  <Bell size={21} />
                </div>
                <div>
                  <h2>Notification Settings</h2>
                  <p>Choose which alerts the platform should enable.</p>
                </div>
              </div>

              <div className="settings-card">
                <div className="settings-card-heading">
                  <h3>Notification Channels</h3>
                  <p>Enable or disable available notification channels.</p>
                </div>

                <SettingRow
                  title="Email Notifications"
                  description="Send notifications through email."
                  checked={settings.emailNotifications}
                  onChange={(value) =>
                    updateSetting("emailNotifications", value)
                  }
                />

                <SettingRow
                  title="SMS Notifications"
                  description="Send important alerts through SMS."
                  checked={settings.smsNotifications}
                  onChange={(value) =>
                    updateSetting("smsNotifications", value)
                  }
                />

                <SettingRow
                  title="Push Notifications"
                  description="Enable app push notifications."
                  checked={settings.pushNotifications}
                  onChange={(value) =>
                    updateSetting("pushNotifications", value)
                  }
                />
              </div>

              <div className="settings-card">
                <div className="settings-card-heading">
                  <h3>Admin Alerts</h3>
                  <p>Choose the events that require your attention.</p>
                </div>

                <SettingRow
                  title="New Registration Alerts"
                  description="Receive alerts when a new member registers."
                  checked={settings.newRegistrationAlert}
                  onChange={(value) =>
                    updateSetting("newRegistrationAlert", value)
                  }
                />

                <SettingRow
                  title="Payment Alerts"
                  description="Receive alerts about membership payments."
                  checked={settings.paymentAlert}
                  onChange={(value) =>
                    updateSetting("paymentAlert", value)
                  }
                />
              </div>
            </>
          )}

          {activeTab === "security" && (
            <>
              <div className="settings-section-heading">
                <div className="settings-section-icon green">
                  <ShieldCheck size={21} />
                </div>
                <div>
                  <h2>Security Settings</h2>
                  <p>Manage login security and administrator sessions.</p>
                </div>
              </div>

              <div className="settings-security-banner">
                <div className="settings-security-banner-icon">
                  <LockKeyhole size={23} />
                </div>
                <div>
                  <h3>Protect your admin account</h3>
                  <p>
                    Use secure credentials and review your login
                    preferences regularly.
                  </p>
                </div>
              </div>

              <div className="settings-card">
                <div className="settings-card-heading">
                  <h3>Login Security</h3>
                  <p>Configure additional security options.</p>
                </div>

                <SettingRow
                  title="Login Alerts"
                  description="Enable alerts for administrator login activity."
                  checked={settings.loginAlerts}
                  onChange={(value) =>
                    updateSetting("loginAlerts", value)
                  }
                />

                <SettingRow
                  title="Two-Factor Authentication"
                  description="Enable an additional verification step for administrator login."
                  checked={settings.twoFactorAuth}
                  onChange={(value) =>
                    updateSetting("twoFactorAuth", value)
                  }
                />

                {renderSelect("Session Timeout", "sessionTimeout", [
                  { value: "15", label: "15 Minutes" },
                  { value: "30", label: "30 Minutes" },
                  { value: "60", label: "60 Minutes" },
                  { value: "120", label: "120 Minutes" },
                ])}
              </div>

              <div className="settings-card settings-password-card">
                <div className="settings-password-icon">
                  <LockKeyhole size={22} />
                </div>
                <div className="settings-password-info">
                  <h3>Administrator Password</h3>
                  <p>
                    Update your administrator password through the
                    secure account management flow.
                  </p>
                </div>
                <button
                  type="button"
                  className="settings-outline-button"
                  onClick={() =>
                    setNotice(
                      "Connect your password change screen or API here."
                    )
                  }
                >
                  Change Password <Eye size={15} />
                </button>
              </div>
            </>
          )}

          <div className="settings-footer">
            <button
              type="button"
              className="settings-reset-button"
              onClick={resetSettings}
            >
              <RotateCcw size={16} />
              Reset
            </button>

            <button type="submit" className="settings-save-button">
              <Save size={17} />
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;